import { useEffect, useMemo, useState } from 'react';

import { api } from '../api/client';
import {
	API_COLLECTION_CARDS_COUNT_URL,
	API_COLLECTION_COUNTS_URL,
	API_ITEMS_COUNT_URL,
} from '../utils';

const buildItemsFilter = collectionId => JSON.stringify({
	field: 'collection_id',
	operator: 'eq',
	value: collectionId,
});

const extractCollectionMeta = (collections = []) => {
	const seen = new Set();
	const ids = [];
	const fallbackMap = {};
	collections.forEach(collection => {
		const rawId = collection?._id || collection?.id;
		if (!rawId || typeof rawId !== 'string' || seen.has(rawId)) return;
		seen.add(rawId);
		ids.push(rawId);
		// Used when an answer leaves a collection's count out.
		fallbackMap[rawId] = {
			cards: Array.isArray(collection?.cards) ? collection.cards.length : 0,
			items: Array.isArray(collection?.items) ? collection.items.length : 0,
		};
	});
	return { ids, fallbackMap };
};

const asNumber = value => (typeof value === 'number' && Number.isFinite(value) ? value : null);

// The API's cap on one counts request (keepr-api utils/queryContract.js
// MAX_BATCH_IDS): more ids is a 400, never a partial answer.
export const COUNTS_BATCH_LIMIT = 100;
// Batch requests in flight at once.
const BATCH_CONCURRENCY = 2;
// Collections counted at once on the per-collection fallback (two requests each).
const PER_COLLECTION_CONCURRENCY = 4;

// Remembered across hook instances: once the batch endpoint 404s, stop
// trying it for the rest of the session.
let batchEndpointAvailable = true;

/** Tests only: forget that the batch endpoint answered 404. */
export function _resetCountsBatchEndpoint() {
	batchEndpointAvailable = true;
}

const chunk = (list, size) => {
	const chunks = [];
	for (let i = 0; i < list.length; i += size) chunks.push(list.slice(i, i + size));
	return chunks;
};

// Runs `worker` over `entries`, at most `limit` at a time. Nothing new starts
// once `isActive()` turns false. `worker` handles its own failures.
async function runPool(entries, limit, worker, isActive) {
	const queue = [...entries];
	const lanes = Array.from({ length: Math.min(limit, queue.length) }, async () => {
		while (queue.length && isActive()) {
			// eslint-disable-next-line no-await-in-loop
			await worker(queue.shift());
		}
	});
	await Promise.all(lanes);
}

/**
 * Card/item counts per collection id: `{ [id]: { cards, items, familyItems?,
 * loading } }`.
 *
 * Asks the batch endpoint (GET /collections/counts?ids=a,b,c ->
 * [{ id, cards, items, familyItemCount? }]) in chunks of COUNTS_BATCH_LIMIT,
 * two chunks at a time. Only a 404 — an API without the endpoint — falls back
 * to the per-collection count endpoints, four collections at a time. Any
 * other failure (a 400, a 429, a network error) leaves those collections'
 * counts unknown: `cards` and `items` null, `loading` false. A refusal is
 * never a reason to ask 226 times instead of twice.
 */
export default function useCollectionCounts(collections = []) {
	const [counts, setCounts] = useState({});
	const { ids, fallbackMap } = useMemo(() => extractCollectionMeta(collections), [collections]);
	const idsKey = useMemo(() => ids.slice().sort().join(','), [ids]);

	useEffect(() => {
		let active = true;

		if (!ids.length) {
			setCounts({});
			return () => { active = false; };
		}

		setCounts(prev => {
			const next = {};
			ids.forEach(id => {
				next[id] = {
					cards: prev[id]?.cards ?? null,
					items: prev[id]?.items ?? null,
					loading: true,
				};
			});
			return next;
		});

		const applyResults = results => {
			if (!active) return;
			setCounts(prev => {
				const next = { ...prev };
				results.forEach(({ id, cards, items, familyItemCount }) => {
					const fallback = fallbackMap[id] || {};
					next[id] = {
						cards: asNumber(cards) ?? fallback.cards ?? 0,
						items: asNumber(items) ?? fallback.items ?? 0,
						// A parent collection's own items plus its sub-collections'
						// (in the caller's read scope); absent otherwise.
						familyItems: asNumber(familyItemCount),
						loading: false,
					};
				});
				return next;
			});
		};

		// Asked about, but not answered: nothing to show, and nothing loading.
		const markUnknown = unknownIds => {
			if (!active || !unknownIds.length) return;
			setCounts(prev => {
				const next = { ...prev };
				unknownIds.forEach(id => {
					next[id] = { cards: null, items: null, familyItems: null, loading: false };
				});
				return next;
			});
		};

		const fetchChunk = async chunkIds => {
			const { data } = await api.get(API_COLLECTION_COUNTS_URL, {
				params: { ids: chunkIds.join(',') },
			});
			const rows = Array.isArray(data) ? data : Object.entries(data || {}).map(
				([id, entry]) => ({ id, ...entry })
			);
			const byId = Object.fromEntries(rows.map(row => [row.id, row]));
			return chunkIds.map(id => ({
				id,
				cards: byId[id]?.cards,
				items: byId[id]?.items,
				familyItemCount: byId[id]?.familyItemCount,
			}));
		};

		const fetchOne = async id => {
			try {
				const [cardsRes, itemsRes] = await Promise.all([
					api.get(API_COLLECTION_CARDS_COUNT_URL(id)),
					api.get(API_ITEMS_COUNT_URL, { params: { filter: buildItemsFilter(id) } }),
				]);
				applyResults([{
					id,
					cards: Number(cardsRes?.data?.count ?? 0),
					items: Number(itemsRes?.data?.count ?? 0),
				}]);
			} catch {
				markUnknown([id]);
			}
		};

		const isActive = () => active;

		(async () => {
			const perCollection = [];
			if (batchEndpointAvailable) {
				await runPool(chunk(ids, COUNTS_BATCH_LIMIT), BATCH_CONCURRENCY, async chunkIds => {
					// A sibling chunk found no endpoint: do not ask it again.
					if (!batchEndpointAvailable) {
						perCollection.push(...chunkIds);
						return;
					}
					try {
						applyResults(await fetchChunk(chunkIds));
					} catch (err) {
						if (err?.response?.status === 404) {
							batchEndpointAvailable = false;
							perCollection.push(...chunkIds);
						} else {
							markUnknown(chunkIds);
						}
					}
				}, isActive);
			} else {
				perCollection.push(...ids);
			}
			if (!active || !perCollection.length) return;
			await runPool(perCollection, PER_COLLECTION_CONCURRENCY, fetchOne, isActive);
		})();

		return () => { active = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [idsKey]);

	return counts;
}
